import type { ReactNode } from 'react';

export type DataTableAlign = 'left' | 'right' | 'center';
export type DataTableFilter = 'text' | 'select' | 'none';
export type DataTableSortDir = 'asc' | 'desc';

export interface DataTableColumn<T> {
  id: string;
  label: string;
  accessor: (row: T) => unknown;
  sortable?: boolean;
  filter?: DataTableFilter;
  filterOptions?: { value: string; label: string }[];
  align?: DataTableAlign;
  width?: number | string;
  render?: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number | boolean | null | undefined;
  /** Clicks in this cell do not fire `onRowClick` (actions, inline editors). */
  stopRowClick?: boolean;
}

export interface DataTableSort {
  id: string;
  dir: DataTableSortDir;
}

export function cellText(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  return String(value);
}

export function compareValues(a: unknown, b: unknown, locale = 'en'): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) && Number.isNaN(b)) {
      return 0;
    }
    if (Number.isNaN(a)) {
      return 1;
    }
    if (Number.isNaN(b)) {
      return -1;
    }
    return a - b;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b), locale, { numeric: true, sensitivity: 'base' });
}

export function applyFilters<T>(
  rows: T[],
  columns: DataTableColumn<T>[],
  filters: Record<string, string>,
): T[] {
  return rows.filter((row) => {
    for (const col of columns) {
      const mode = col.filter ?? 'text';
      if (mode === 'none') {
        continue;
      }
      const query = (filters[col.id] || '').trim();
      if (!query) {
        continue;
      }
      const text = cellText(col.accessor(row));
      if (mode === 'select') {
        if (text !== query) {
          return false;
        }
      } else if (!text.toLowerCase().includes(query.toLowerCase())) {
        return false;
      }
    }
    return true;
  });
}

export function applySort<T>(
  rows: T[],
  columns: DataTableColumn<T>[],
  sort: DataTableSort | null,
  locale = 'en',
): T[] {
  if (!sort) {
    return rows;
  }
  const col = columns.find((c) => c.id === sort.id);
  if (!col || col.sortable === false) {
    return rows;
  }
  const copy = [...rows];
  copy.sort((left, right) => {
    const av = col.sortValue ? col.sortValue(left) : col.accessor(left);
    const bv = col.sortValue ? col.sortValue(right) : col.accessor(right);
    if (av == null && bv == null) {
      return 0;
    }
    if (av == null) {
      return 1;
    }
    if (bv == null) {
      return -1;
    }
    const cmp = compareValues(av, bv, locale);
    return sort.dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

export function selectOptionsForColumn<T>(
  rows: T[],
  col: DataTableColumn<T>,
  locale = 'en',
): { value: string; label: string }[] {
  if (col.filterOptions && col.filterOptions.length > 0) {
    return col.filterOptions;
  }
  const seen = new Set<string>();
  const opts: { value: string; label: string }[] = [];
  for (const row of rows) {
    const text = cellText(col.accessor(row));
    if (!text || seen.has(text)) {
      continue;
    }
    seen.add(text);
    opts.push({ value: text, label: text });
  }
  opts.sort((a, b) => a.label.localeCompare(b.label, locale, { sensitivity: 'base' }));
  return opts;
}
